import {
  Component, OnInit, OnDestroy, ChangeDetectionStrategy,
  inject, signal, computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { GetLocalStorageValues } from 'src/app/shared/util/local-storage';
import { FeatureAnnouncementService, FeatureAnnouncement } from 'src/app/shared/services/feature-announcement.service';

@Component({
  selector: 'app-feature-announcement-modal',
  templateUrl: './feature-announcement-modal.component.html',
  styleUrls: ['./feature-announcement-modal.component.scss'],
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FeatureAnnouncementModalComponent implements OnInit, OnDestroy {
  private readonly service = inject(FeatureAnnouncementService);
  private readonly localStorage = new GetLocalStorageValues();
  private readonly destroy$ = new Subject<void>();

  readonly visible = signal(false);
  readonly features = signal<FeatureAnnouncement[]>([]);
  readonly activeIndex = signal(0);

  readonly currentFeature = computed(() => this.features()[this.activeIndex()]);
  readonly isLast = computed(() => this.activeIndex() >= this.features().length - 1);
  readonly isSingle = computed(() => this.features().length === 1);
  readonly dotCount = computed(() => this.features().length);

  private parentClubId = '';

  ngOnInit(): void {
    this.parentClubId = this.localStorage.getPostgreParentClubId();
    if (!this.parentClubId) return;

    this.service.getActive(this.parentClubId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res.show_modal && res.features.length) {
            this.features.set(res.features);
            this.activeIndex.set(0);
            this.visible.set(true);
          }
        },
      });
  }

  ngOnDestroy(): void { this.destroy$.next(); this.destroy$.complete(); }

  next(): void {
    if (!this.isLast()) {
      this.activeIndex.update((i) => i + 1);
    }
  }

  prev(): void {
    if (this.activeIndex() > 0) {
      this.activeIndex.update((i) => i - 1);
    }
  }

  dismiss(): void {
    const ids = this.features().map((f) => f.id);
    this.service.markShown(this.parentClubId, ids)
      .pipe(takeUntil(this.destroy$))
      .subscribe();
    this.visible.set(false);
  }

  suppress(): void {
    this.service.suppress(this.parentClubId)
      .pipe(takeUntil(this.destroy$))
      .subscribe();
    this.visible.set(false);
  }
}
